import type { PDFPage } from 'pdf-lib'
import type { Annotation } from '@/types'

// "View space" is the page as pdf.js renders it: crop box intersected with
// the media box, /Rotate applied. Annotations are stored in view space
// (top-left origin); buildPdf draws in it with a bottom-left origin.

function viewBox(page: PDFPage) {
  const media = page.getMediaBox()
  const crop = page.getCropBox()
  const x0 = Math.max(media.x, crop.x)
  const y0 = Math.max(media.y, crop.y)
  const x1 = Math.min(media.x + media.width, crop.x + crop.width)
  const y1 = Math.min(media.y + media.height, crop.y + crop.height)
  if (x1 <= x0 || y1 <= y0) {
    return { x0: media.x, y0: media.y, x1: media.x + media.width, y1: media.y + media.height }
  }
  return { x0, y0, x1, y1 }
}

function pageRotation(page: PDFPage): 0 | 90 | 180 | 270 {
  return ((((Math.round(page.getRotation().angle / 90) * 90) % 360) + 360) % 360) as 0 | 90 | 180 | 270
}

// Width/height of the page in view space (what the viewer shows).
export function viewSize(page: PDFPage): { width: number; height: number } {
  const { x0, y0, x1, y1 } = viewBox(page)
  const rotated = pageRotation(page) % 180 !== 0
  return rotated ? { width: y1 - y0, height: x1 - x0 } : { width: x1 - x0, height: y1 - y0 }
}

// Matrix [a b c d e f] mapping view space (bottom-left origin) into the
// page's user space. Mirrors pdf.js's PageViewport so annotations placed on
// screen land in the same spot in the exported file.
export function viewSpaceMatrix(page: PDFPage): [number, number, number, number, number, number] {
  const { x0, y0, x1, y1 } = viewBox(page)
  switch (pageRotation(page)) {
    case 90: return [0, 1, -1, 0, x1, y0]
    case 180: return [-1, 0, 0, -1, x1, y1]
    case 270: return [0, -1, 1, 0, x0, y1]
    default: return [1, 0, 0, 1, x0, y0]
  }
}

type Rect = { x: number; y: number; w: number; h: number }

// Rotate a rect by 90° inside a view of `width` x `height` (pre-rotation).
function rotateRect(r: Rect, dir: 'cw' | 'ccw', width: number, height: number): Rect {
  return dir === 'cw'
    ? { x: height - r.y - r.h, y: r.x, w: r.h, h: r.w }
    : { x: r.y, y: width - r.x - r.w, w: r.h, h: r.w }
}

// Re-position an annotation after its page was rotated by 90°. Regions tied
// to page content (redactions, drawings, text-edit covers) rotate with the
// page; upright items (text, signatures, images, the text-edit editor) keep
// their size and orientation and move with their centre.
export function rotateAnnotation(
  a: Annotation,
  dir: 'cw' | 'ccw',
  width: number,
  height: number,
): Annotation {
  const full = rotateRect(a, dir, width, height)
  const centred = {
    x: full.x + full.w / 2 - a.w / 2,
    y: full.y + full.h / 2 - a.h / 2,
  }
  switch (a.type) {
    case 'redaction':
      return { ...a, ...full }
    case 'drawing':
      return {
        ...a,
        ...full,
        points: a.points.map(([px, py]) => (dir === 'cw' ? [a.h - py, px] : [py, a.w - px])),
      }
    case 'textEdit': {
      const cover = rotateRect(
        { x: a.origX ?? a.x, y: a.origY ?? a.y, w: a.origW ?? a.w, h: a.origH ?? a.h },
        dir, width, height,
      )
      return { ...a, ...centred, origX: cover.x, origY: cover.y, origW: cover.w, origH: cover.h }
    }
    default:
      return { ...a, ...centred }
  }
}
